import { Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import GISAddressAutoComplete from "components/Address/GISAddressAutoComplete";
import Address from "entities/Address";
import { useEffect, useMemo, useState } from "react";
import { Option } from "react-bootstrap-typeahead/types/types";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "store/configureStore";
import { AddressRecord } from "../../../../../entities/CommunicationInfo";
import Country from "../../../../../entities/Country";
import { getCountries } from "../../../../../store/slices/address";
import ModalHeaderWithActions from "../../../../UI/ModalHeaderWithActions";
import TypeaheadWithIcon from "../../../../UI/TypeaheadWithIcon";
import "../ChangeDialogs.css";
import ProfileEditContentNode from "../ProfileEditContentNode";
import './ChangeContactAddress.css';
import EditExistingAddressForm from "./EditExistingAddressForm";

interface CountryOption {
    id: number;
    label: string;
    item: Country;
}

interface Props {
    currentAddress: AddressRecord | undefined;
    show: boolean;
    onClose?: () => void;
    onSubmit: (data: AddressRecord) => void;
    editExisting?: boolean;
}

// Define field validation structure
interface FieldValidation {
    valid: boolean;
    touched: boolean;
    errorMessage: string;
}

// Define validation state structure
type ValidationState = {
    [key in keyof AddressRecord]?: FieldValidation;
};

const ChangeContactAddress = (props: Props) => {

    const { t } = useTranslation("parts");
    const dispatch = useDispatch<AppDispatch>();
    const { show, onClose = () => { }, onSubmit = () => { } } = props;

    const emptyAddress: AddressRecord = {
        Id: 0,
        City: "",
        Country: "Estonia",
        State: "",
        County: "",
        Street: "",
        ZipCode: "",
        FullAddress: "",
        Adob_Id: null,
    };

    const editExisting = props.editExisting || false;
    const [currentAddress, setCurrentAddress] = useState<AddressRecord>(props.currentAddress || emptyAddress);
    const [addressText, setAddressText] = useState("");

    useEffect(() => {
        if (props.currentAddress) { setCurrentAddress(props.currentAddress) }
    }, [props.currentAddress])

    // Initialize validation state with all required fields
    const initialValidation: ValidationState = {
        Country: { valid: true, touched: false, errorMessage: "" },
        City: { valid: true, touched: false, errorMessage: "" },
        Street: { valid: true, touched: false, errorMessage: "" },
        ZipCode: { valid: true, touched: false, errorMessage: "" },
        State: { valid: true, touched: false, errorMessage: "" },
        County: { valid: true, touched: false, errorMessage: "" },
        FullAddress: { valid: true, touched: false, errorMessage: "" }
    };

    const [validation, setValidation] = useState<ValidationState>(initialValidation);

    const { list: countries } = useSelector(
        (state: AppState) => state.entities.address.countries
    );

    const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(
        countries.find(c => c.Name === currentAddress.Country) ||
        countries.find(c => c.Code === "EST")
    );

    useEffect(() => {
        if (show && countries.length === 0) {
            dispatch(getCountries());
        }
    }, [show, countries.length, dispatch]);

    useEffect(() => {
        const selected = countries.find(c => c.Name === currentAddress.Country) ||
            countries.find(c => c.Code === "EST")
        setSelectedCountry(selected);
    }, [countries, props.currentAddress]);

    const validateField = (field: keyof AddressRecord, value: string, dontTouch: boolean = false): FieldValidation => {
        let valid = true;
        let errorMessage = "";

        switch (field) {
            case "Country":
            case "City":
            case "Street":
                valid = value.trim() !== "";
                errorMessage = valid ? "" : t("common:form.errors.required");
                break;
            case "FullAddress":
                valid = value.trim() !== "";
                errorMessage = valid ? "" : t("common:form.errors.required");
                break;
            case "ZipCode":
                if (value.trim() === "") {
                    valid = false;
                    errorMessage = t("common:form.errors.required");
                } else if (!/^\d+$/.test(value)) {
                    valid = false;
                    errorMessage = t("common:form.errors.invalidNumber");
                }
                break;
            default:
                valid = true;
                errorMessage = "";
        }

        return { valid, touched: !dontTouch, errorMessage };
    };

    useEffect(() => {
        if (props.show) {

            const input = props.currentAddress || emptyAddress;
            setCurrentAddress(input);

            const newValidation = { ...validation };
            for (const field of Object.keys(input) as Array<keyof AddressRecord>) {
                newValidation[field] = validateField(field, input[field]?.toString() || "", true);
            }

            setValidation(newValidation);
        }
    }, [props.show]);

    const canSubmit = useMemo(() => {
        if (selectedCountry?.Code === 'EST') {
            return !!(currentAddress.Country && currentAddress.County);
        } else {
            return !!(currentAddress.FullAddress && currentAddress.Country && currentAddress.City);
        }


    }, [currentAddress.City, currentAddress.Country, currentAddress.FullAddress, selectedCountry?.Code, currentAddress.County]);

    const handleSubmit = () => {
        if (!canSubmit) {
            return;
        }

        const updatedAddress = {
            ...currentAddress,
        };

        if (addressText && addressText.length > 0) {
            updatedAddress.FullAddress = addressText;
        }

        updatedAddress.CountryCode = selectedCountry?.Code

        onSubmit(updatedAddress);
    };

    const handleChange = (field: keyof AddressRecord, value: string) => {
        setCurrentAddress((prev) => ({
            ...prev,
            [field]: value
        }));

        setValidation(prev => ({
            ...prev,
            [field]: validateField(field, value)
        }));
    };


    const handleAddressRequest = (address: Address | null) => {
        if (!address) {
            return;
        }

        let fullAddressText = address.AddressText ?? "";
        if (address.ZipCode && fullAddressText && !fullAddressText.includes(address.ZipCode)) {
            fullAddressText = `${fullAddressText}, ${address.ZipCode}`;
        }

        const updatedAddress = {
            ...currentAddress,
            Street: address.Street,
            State: address.State,
            County: address.County,
            City: address.City,
            Country: address.Country,
            ZipCode: address.ZipCode,
            Adob_Id: address.Adob_Id,
            FullAddress: fullAddressText
        };
        setCurrentAddress(updatedAddress);
        setAddressText(fullAddressText);

        const newValidation = { ...validation };
        for (const field of Object.keys(updatedAddress) as Array<keyof AddressRecord>) {
            newValidation[field] = validateField(field, updatedAddress[field]?.toString() || "", true);
        }

        setValidation(newValidation);
    };

    const GetCountriesOptions = (): CountryOption[] => {
        return countries.map(item => ({
            id: item.Id,
            label: item.Name,
            item,
        }));
    }

    const countriesEntries = useMemo(() => GetCountriesOptions(), [countries]);

    const handleCountryChange = (selected: Option[]) => {
        const typedSelected = selected as CountryOption[];
        if (typedSelected.length > 0) {
            setSelectedCountry(typedSelected[0].item)
            handleChange("Country", typedSelected[0].label)
        } else {
            setSelectedCountry(undefined);
            handleChange("Country", '');
        }
    };

    const handleClearClick = () => {
        setCurrentAddress(emptyAddress);
        setAddressText("");
    }

    const handleFieldUpdate = (field: keyof AddressRecord, value: string) => {
        setCurrentAddress((prev) => { 
            const updated = { ...prev, [field]: value };
             const parts = [
                updated.Street,
                updated.City,
                selectedCountry?.Code
            ].filter(Boolean);

            updated.FullAddress = parts.join(", ");

            return updated;
        });

        setValidation(prev => ({
            ...prev,
            [field]: validateField(field, value)
        }));
       
    }

    // Reset currentAddress and validation when selectedCountry changes
    useEffect(() => {
        if (!selectedCountry) return;

        setCurrentAddress(prev => ({
            ...prev,
            Country: selectedCountry.Name
        }));
    }, [selectedCountry]);


    return show && (
        <Modal show={show} onHide={onClose}
            className="change-modal change-contact-address">
            <ModalHeaderWithActions
                title={t("pages:profile.homeAddress")}
                onClose={onClose}
                onSubmit={handleSubmit}
                canSubmit={canSubmit}
            />
            <Modal.Body>
                <Form>
                    <ProfileEditContentNode
                        label={t("pages:profile.countryName")}
                        hideClearButton={true}
                        handleClear={() => { }}
                    >
                        <TypeaheadWithIcon
                            id="formCountry"
                            onChange={handleCountryChange}
                            options={countriesEntries}
                            selected={selectedCountry ? [{ id: selectedCountry.Id, label: selectedCountry.Name, country: selectedCountry }] : []}
                            placeholder={t("common:form.placeholders.enterData")}
                            labelKey="label"
                            className="form-control-input-custom"
                        />
                    </ProfileEditContentNode>


                    {selectedCountry?.Code === 'EST' ? (
                        editExisting ? (
                            <>
                                <EditExistingAddressForm 
                                    currentAddress={currentAddress}
                                    onAddressChange={setCurrentAddress}
                                />
                            </>
                        ) : (
                            <>
                                <div className="dataNode-label">{t("common:addressSearch")}</div>
                                <GISAddressAutoComplete
                                    value={currentAddress?.FullAddress || undefined}
                                    onAddressRequest={handleAddressRequest}
                                    onChange={setAddressText}
                                    onClearClick={handleClearClick}
                                />
                            </>
                        )
                    ) : (
                        <>
                            <Form.Group controlId="foreignCity" className="mb-3">
                                <Form.Label className="dataNode-label">
                                    {t("pages:profile.cityName")}
                                </Form.Label>
                                <Form.Control
                                    className="form-control-input-custom"
                                    type="text"
                                    name="city"
                                    value={currentAddress?.City || ""}
                                    onChange={(e) => handleFieldUpdate("City", e.target.value)}
                                    data-lpignore="true" 
                                    data-1p-ignore="true"
                                    autoComplete="off"
                                />

                            </Form.Group>
                            <Form.Group controlId="foreignAddress">
                                <Form.Label className="dataNode-label">
                                    {t("common:addressInput")}
                                </Form.Label>
                                <Form.Control
                                    className="form-control-input-custom"
                                    type="text"
                                    name="fullAddress"
                                    value={currentAddress?.Street || ""}
                                    onChange={(e) => handleFieldUpdate("Street", e.target.value)}
                                    data-lpignore="true" 
                                    data-1p-ignore="true"
                                    autoComplete="off"

                                />
                            </Form.Group>
                        </>
                    )}
                </Form>
            </Modal.Body>
        </Modal>
    );
};
export default ChangeContactAddress;
